import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Hand, Scan, Check, Package, MapPin, RefreshCw, User } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import * as greigeStorageApi from '@/services/greigeStorageApi';
import * as validationService from '@/services/validationService';
import { StaffPickerSelect } from '@/components/StaffPickerSelect';

const Picking = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialSlipId = searchParams.get('slip');
  
  const [pickSlips, setPickSlips] = useState([]);
  const [selectedSlip, setSelectedSlip] = useState(null);
  const [loading, setLoading] = useState(true);
  const [staff, setStaff] = useState([]);
  const [selectedPicker, setSelectedPicker] = useState('');
  const [bundleBarcode, setBundleBarcode] = useState('');
  const [scanning, setScanning] = useState(false);
  const barcodeRef = useRef(null);

  const fetchPickSlips = async (reselectId = initialSlipId) => {
    try {
      setLoading(true);
      const data = await greigeStorageApi.getPickSlips();
      const activeSlips = (data.pick_slips || []).filter(
        s => s.status !== 'completed'
      );
      setPickSlips(activeSlips);
      
      if (reselectId) {
        const slip = activeSlips.find(s => s.pick_slip_id === reselectId);
        if (slip) setSelectedSlip(slip);
      }
    } catch (error) {
      toast.error('Failed to fetch pick slips');
    } finally {
      setLoading(false);
    }
  };

  const fetchStaff = async () => {
    try {
      const data = await validationService.getStaffList(null, null, 5000);
      setStaff(data.staff || []);
    } catch (error) {
      console.error('Failed to fetch staff:', error);
    }
  };

  useEffect(() => {
    fetchPickSlips();
    fetchStaff();
  }, []);

  const handleAssignPicker = async () => {
    if (!selectedPicker) {
      toast.error('Please select a picker');
      return;
    }
    try {
      await greigeStorageApi.assignPicker(selectedSlip.pick_slip_id, selectedPicker);
      toast.success('Picker assigned');
      await fetchPickSlips(selectedSlip.pick_slip_id);
      setTimeout(() => barcodeRef.current?.focus(), 100);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign');
    }
  };

  const handleScanBundle = async (e) => {
    if (e.key !== 'Enter' || !bundleBarcode.trim()) return;
    
    try {
      setScanning(true);
      const result = await greigeStorageApi.pickBundle(selectedSlip.pick_slip_id, bundleBarcode.trim());
      
      if (result.all_picked) {
        toast.success('All bundles picked! Pick slip completed.');
        setSelectedSlip(null);
        fetchPickSlips();
      } else {
        const subTxt = result.substituted_from ? ` — substituted from ${result.substituted_from}` : '';
        toast.success(`Bundle picked (${result.picked_count}/${result.total_count})${subTxt}`);
        // Refresh the slip data
        const data = await greigeStorageApi.getPickSlips();
        const updatedSlip = data.pick_slips.find(s => s.pick_slip_id === selectedSlip.pick_slip_id);
        if (updatedSlip) setSelectedSlip(updatedSlip);
      }
      
      setBundleBarcode('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to pick bundle');
      setBundleBarcode('');
    } finally {
      setScanning(false);
      barcodeRef.current?.focus();
    }
  };

  const pickedCount = selectedSlip?.bundles?.filter(b => b.picked).length || 0;
  const totalCount = selectedSlip?.bundles?.length || 0;
  const progress = totalCount > 0 ? (pickedCount / totalCount) * 100 : 0;

  return (
    <div className="min-h-screen bg-[#FFFBF5]">
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              onClick={() => selectedSlip ? setSelectedSlip(null) : navigate('/sections/greige-storage')} 
              className="text-[#F9FAFB] hover:bg-white/10"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold font-display">Picking</h1>
              <p className="text-sm text-[#E8DCC8]">Greige Storage</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 md:px-6 py-8">
        {!selectedSlip ? (
          // Pick Slip Selection
          <>
            <div className="mb-6 flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold text-[#1A1F2E] font-display">Select Pick Slip</h2>
                <p className="text-[#5A5F6E]">Choose a pick slip to start picking</p>
              </div>
              <Button variant="outline" onClick={() => fetchPickSlips()} disabled={loading}>
                <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-12">
                <RefreshCw className="w-8 h-8 animate-spin text-[#2D5F4F]" />
              </div>
            ) : pickSlips.length === 0 ? (
              <Card className="border-[#E5DFD0]">
                <CardContent className="py-12 text-center">
                  <Hand className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                  <h3 className="text-lg font-semibold text-[#1A1F2E] mb-2">No Active Pick Slips</h3>
                  <p className="text-[#5A5F6E]">Generate pick slips from shipment requests</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4">
                {pickSlips.map((slip) => (
                  <Card 
                    key={slip.pick_slip_id} 
                    className="border-[#E5DFD0] hover:border-[#2D5F4F] cursor-pointer transition-colors"
                    onClick={() => setSelectedSlip(slip)}
                  >
                    <CardContent className="py-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-semibold text-[#1A1F2E]">{slip.pick_slip_id}</p>
                          <p className="text-sm text-[#5A5F6E]">
                            {slip.source_design_name} • {slip.color} • {slip.bundles?.length} bundles
                          </p>
                        </div>
                        <div className="text-right">
                          <Badge className={slip.assigned_picker ? 'bg-yellow-100 text-yellow-700' : 'bg-blue-100 text-blue-700'}>
                            {slip.assigned_picker ? `Assigned: ${slip.assigned_picker_name}` : 'Needs Assignment'}
                          </Badge>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </>
        ) : (
          // Picking Interface
          <>
            {/* Slip Info */}
            <Card className="border-[#E5DFD0] mb-6">
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center justify-between">
                  <span>{selectedSlip.pick_slip_id}</span>
                  <Badge>{selectedSlip.status}</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 gap-4 text-sm mb-4">
                  <div>
                    <p className="text-xs text-[#5A5F6E]">Design</p>
                    <p className="font-medium">{selectedSlip.source_design_name}</p>
                  </div>
                  <div>
                    <p className="text-xs text-[#5A5F6E]">Color</p>
                    <p className="font-medium">{selectedSlip.color}</p>
                  </div>
                  <div>
                    <p className="text-xs text-[#5A5F6E]">Progress</p>
                    <p className="font-medium">{pickedCount} / {totalCount}</p>
                  </div>
                </div>
                <Progress value={progress} className="h-2" />
              </CardContent>
            </Card>

            {/* Assign Picker */}
            {!selectedSlip.assigned_picker && (
              <Card className="border-[#E5DFD0] mb-6">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <User className="w-5 h-5" />
                    Assign Picker
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <StaffPickerSelect staff={staff} value={selectedPicker} onChange={setSelectedPicker}
                      className="flex-1" testId="picking-assign-picker" />
                    <Button onClick={handleAssignPicker} className="bg-[#2D5F4F] hover:bg-[#1A1F2E]" data-testid="picking-assign-btn">
                      Assign
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Scanning Interface */}
            {selectedSlip.assigned_picker && (
              <>
                <Card className="border-[#E5DFD0] mb-6">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Scan className="w-5 h-5" />
                      Scan Bundle
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center gap-3 mb-4 p-3 bg-green-50 rounded-lg">
                      <User className="w-5 h-5 text-green-600" />
                      <span className="text-green-700">Picker: {selectedSlip.assigned_picker_name}</span>
                    </div>
                    <Input
                      ref={barcodeRef}
                      value={bundleBarcode}
                      onChange={(e) => setBundleBarcode(e.target.value)}
                      onKeyDown={handleScanBundle}
                      placeholder="Scan bundle barcode..."
                      disabled={scanning}
                      data-testid="bundle-barcode-input"
                    />
                    <p className="text-xs text-amber-700 mt-2" data-testid="any-bundle-relaxation-hint">
                      Tip: until the Greige Warehouse is ready, you may scan <strong>any bundle</strong> matching this slip's
                      design{selectedSlip.color ? ` & colour (${selectedSlip.color})` : ''}.
                      The slip will substitute the scanned bundle into an open slot automatically.
                    </p>
                  </CardContent>
                </Card>

                {/* Bundles to Pick */}
                <Card className="border-[#E5DFD0]">
                  <CardHeader>
                    <CardTitle>Bundles to Pick</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2 max-h-96 overflow-y-auto">
                      {selectedSlip.bundles?.map((bundle, idx) => (
                        <div 
                          key={bundle.bundle_barcode}
                          className={`flex items-center justify-between p-3 rounded-lg border ${
                            bundle.picked ? 'bg-green-50 border-green-200' : 'bg-white border-gray-200'
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <span className="text-xs text-gray-500 w-6">{idx + 1}</span>
                            {bundle.picked ? (
                              <Check className="w-5 h-5 text-green-600" />
                            ) : (
                              <Package className="w-5 h-5 text-gray-400" />
                            )}
                            <div>
                              <p className="font-mono text-sm font-medium">{bundle.bundle_barcode}</p>
                              <p className="text-xs text-gray-500 flex items-center gap-1">
                                <MapPin className="w-3 h-3" />
                                {bundle.footprint}
                              </p>
                              {bundle.substituted_from && bundle.substituted_from !== bundle.bundle_barcode && (
                                <p className="text-[10px] text-amber-700 mt-0.5" data-testid={`substituted-from-${idx}`}>
                                  Substituted from {bundle.substituted_from}
                                </p>
                              )}
                            </div>
                          </div>
                          {bundle.picked && (
                            <Badge className="bg-green-100 text-green-700 border-0 text-xs">
                              Picked
                            </Badge>
                          )}
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </>
            )}
          </>
        )}
      </main>
    </div>
  );
};

export default Picking;
